import '@/styles/components.scss'
import '@/styles/globals.scss'
import '@/styles/seafi-treasury.scss'
import '@rainbow-me/rainbowkit/styles.css'

import { ChakraProvider } from '@chakra-ui/react'
import { RainbowKitProvider, darkTheme, lightTheme } from '@rainbow-me/rainbowkit'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Analytics } from '@vercel/analytics/next'
import dynamic from 'next/dynamic'
import { Host_Grotesk } from 'next/font/google'
import Head from 'next/head'
import { WagmiProvider } from 'wagmi'

import { ChakraFonts } from '@/common/components/CustomFont'
import { PageHead } from '@/common/components/PageHead'
import { config } from '@/config/wagmi'
import { ThemeProvider, useTheme } from '@/design-system/ThemeProvider'
import theme from '@/theme'

// RainbowKit styled to match the SeaFi Treasury cluster: fully-rounded pills, no border/shadow,
// following the app's resolved light/dark palette. Action colour is INK (the app's action colour,
// matching the active nav pill) — the orange accent stays reserved for the user's own value.
const RainbowKitThemed = ({ children }: { children: React.ReactNode }) => {
  const { resolved } = useTheme()
  const dark = resolved === 'dark'
  // ink acts as the button fill; paper as its text — inverts cleanly between themes.
  const ink = dark ? '#eceef2' : '#212329'
  const paper = dark ? '#16181c' : '#ffffff'
  const base = dark
    ? darkTheme({ accentColor: ink, accentColorForeground: paper, overlayBlur: 'small' })
    : lightTheme({ accentColor: ink, accentColorForeground: paper, overlayBlur: 'small' })
  const fill = dark ? '#22252b' : '#f2f2f2'
  const rkTheme = {
    ...base,
    radii: { ...base.radii, connectButton: '999px', actionButton: '999px', menuButton: '999px' },
    shadows: { ...base.shadows, connectButton: 'none' },
    colors: { ...base.colors, connectButtonBackground: fill, connectButtonInnerBackground: fill },
  }
  return (
    <RainbowKitProvider showRecentTransactions theme={rkTheme}>
      {children}
    </RainbowKitProvider>
  )
}

// Create a dynamic component that disables SSR for the entire app content
const DynamicAppContent = dynamic(
  () => Promise.resolve(({ children }: { children: React.ReactNode }) => <>{children}</>),
  { ssr: false },
)

const queryClient = new QueryClient()

const hostGrotesk = Host_Grotesk({
  display: 'swap',
  subsets: ['latin'],
  variable: '--font-host-grotesk',
  weight: ['400', '300', '500', '600', '700', '800'],
})

export const App = ({ Component, pageProps }) => {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout || ((page) => page)

  return (
    <DynamicAppContent>
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <RainbowKitThemed>
              <ChakraProvider theme={theme}>
                <ChakraFonts />
                <Head>
                  <meta
                    content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
                    name="viewport"
                  />
                  <PageHead />
                </Head>
                <main className={`h-full ${hostGrotesk.variable}`}>
                  {getLayout(
                    <>
                      <Component {...pageProps} />
                      <Analytics />
                    </>,
                  )}
                </main>
              </ChakraProvider>
            </RainbowKitThemed>
          </ThemeProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </DynamicAppContent>
  )
}

export default App
